import type {
	SpacingTypes,
	ColorTypes,
	SizeTypes,
	FontTypes,
	TextTypes,
	TypograhyTypes,
	IconSizeTypes,
	AvtarSizeTypes,
} from './styled';
import { DefaultTheme } from 'styled-components';

const theme = (currentTheme: DefaultTheme) => {
	const spacing: SpacingTypes = {
		sm1: '4px',
		sm2: '8px',
		rg1: '12px',
		rg2: '16px',
		lg1: '20px',
		lg2: '24px',
		ma1: '32px',
		ma2: '48px',
		ma3: '64px',
	};

	const color: ColorTypes = {
		//variant description
		primary: '#255AA5', // brand Color
		warning: '#FDB849',
		success: '#1ED760',
		info: '#143E9F',
		danger: '#FF3350',
		white: '#ffffff',
		notification: '#01DFC2',
		feedback: '#143E9F',
		status: '#AFB4BB',

		//common

		background: '#ffffff',
		text: 'black',
		gray: {
			90: '#1D2530',
			80: '#343C46',
			70: '#474F5C',
			60: '#636972',
			50: '#91979F',
			40: '#AFB4BB',
			30: '#CBD0D6',
			20: '#DFE5EC',
			10: '#EEF1F7',
			0: '#FFFFFF',
		},
	};

	//This is for font size
	const size: SizeTypes = {
		ma2: '32px', // massive
		ma1: '28px',
		lg2: '24px', //large,
		lg1: '20px',
		rg3: '16px', // regular,
		rg2: '14px',
		rg1: '13px',
		sm2: '12px',
		sm1: '10px',
	};

	//This is for icon size

	const iconSize: IconSizeTypes = {
		xs: '24px',
		sm: '32px',
		rg: '48px',
		lg: '60px',
	};

	const avtarSize: AvtarSizeTypes = {
		sm: '48px',
		rg: '96px',
		lg: '160px',
	};

	const avtarPlaceholderSize: AvtarSizeTypes = {
		sm: '16px',
		rg: '32px',
		lg: '48px',
	};

	const font: FontTypes = {
		source:
			'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap',
		family: 'Inter, sans-serif',
		weight: {
			bold: 700,
			semiBold: 600,
			medium: 500,
			regular: 400,
			light: 300,
		},
	};

	const text: TextTypes = {
		fontSize: size.rg1,
		color: color.text,
		letterSpacing: 'normal',
		lineHeight: 1,
		fontWeight: font.weight.regular,
	};

	const typography: TypograhyTypes = {
		pageHeader: `
		color: ${color.gray[80]};
		font-size:${size.ma1};
		line-height: 1.3;
		font-weight:${font.weight.medium};
		`,
		formLabel: `
        color:${color.gray[90]};
        font-size:${size.rg1};
        letter-spacing:0;
        font-weight:${font.weight.medium};
        line-height:1.5;
		`,
		formLabel2: `
		color:${color.gray[90]};
        font-size:${size.rg2};
        letter-spacing:0;
        font-weight:${font.weight.medium};
        line-height:1.5;
		`,
		input: `
        color:${color.gray[70]};
        font-size:${size.rg2};
        letter-spacing:0;
        font-weight:${font.weight.medium};
        lineHeight:1.5;
		`,
		breadCrumbs: `
        color:${color.text};
        font-size:${size.sm2};
        letter-spacing:0;
        line-height:1.5;
        font-weight:${font.weight.regular};
		`,
		btnText: `
        color:${color.text};
        font-size:${size.rg2};
        line-height:1.5;
        line-spacing:0;
        font-weight:${font.weight.medium};
		`,
		textOnlyBtnText: `
        color:${color.text};
        font-size:${size.rg1};
        font-weight:${font.weight.medium}
        letterSpacing:0;
        lineHeight:1.5;
		`,
		statsValue: `
        color:${color.gray[80]};
        font-size:${size.rg1};
        letter-spacing:0;
        line-height:1.5;

        font-weight:${font.weight.medium};
		`,
		statsLabel: `
        color:${color.gray[60]};
        font-size:${size.rg1};
        letter-spacing:0;
        line-height:1.5;

        font-weight: ${font.weight.medium};
		`,
		menuItem: `
        color:${color.text};
        font-size:${size.rg1};
        letter-spacing:0;
        line-height:1.5;
        font-weight: ${font.weight.regular};
		`,

		collectionHeader: `
        color:${color.text};
        font-size:${size.rg3};
        letter-spacing:0;
        line-height:1.3;
        font-weight: ${font.weight.medium};
		`,
		feedbackText: `
        color:${color.gray[60]};
        font-size:${size.rg1};
        letter-spacing:0;
        line-height:1.5;
        font-weight: ${font.weight.regular};
		`,
		link: `
		color:${color.primary};
        font-size:${size.rg1};
        letter-spacing:0;
        line-height:1.5;
        font-weight: ${font.weight.medium};
    `,

		bodyLarge: `
		font-size: ${size.rg3};
		letter-spacing: 0;
		font-weight: ${font.weight.regular};
		line-height: 1.5;
    `,

		bodyRegular: `
		color: ${color.gray[80]};
		font-size: ${size.rg2};
		font-weight: ${font.weight.regular};
		letter-spacing: 0;
		line-heigth: 1.5;
    `,

		bodySmall: `
		font-size: ${size.sm2};
		font-weight: ${font.weight.regular};
		letter-spacing: 0;
		line-heigth: 1.5;

    `,

		tableMain: `
		font-size: ${size.rg2};
		font-weight: ${font.weight.medium};
		letter-spacing: 0;
		line-heigth: 1.5;

    `,

		statsHeader: `
		font-size: ${size.rg2};
		font-weight: ${font.weight.medium};
		letter-spacing: 0;
		line-heigth: 1.5;

    `,
	};

	const customTheme = {
		...currentTheme,
		color,
		size,
		iconSize,
		avtarSize,
		avtarPlaceholderSize,
		text,
		font,
		btn: {
			primary: 'color',
		},
		spacing,
		typography,
		boxShadow: {
			lg: '0px 4px 20px rgba(52, 60, 70, 0.2)',
			rg: '0px 4px 10px rgba(52, 60, 70, 0.1)',
		},
		checkbox: {
			label: `${typography.input}`,
		},
		input: `${typography.input}`,
		select: {
			option: {
				color: color.text,
				fontSize: size.rg2,
				letterSpacing: 0,
				fontWeight: font.weight.regular,
			},
		},
	};
	return customTheme;
};
export default theme;
